import { useContext, useEffect, useMemo, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import DocumentAnalysisLoadingPopup from "../../components/Onboarding/DocumentAnalysisLoading";
import {
  getExplainedDocCanonicalKeys,
  canonicalizeMissingDocKey,
  isCanonicalKeyCovered,
} from "../../Utils/missingDocNote";
import TenantContext from "../../context/tenant/TenantContext";
import {
  getDocumentsFromEmploymentType,
  getPayslipDateWarning,
  haveValue,
  toast,
  getRequiredPayslipPeriods,
  getRequiredVatQuarterPeriods,
  removeUuidFromFileName,
  isCompanyTenantOwnDocument,
  isIdentityDocument,
} from "../../helper/common";
import { docsMap } from "../../Utils/skorLiteDocumentMap";
import { useNavigate } from "react-router-dom";
import docsData from "../../Utils/docsData";
import ContinueAddingPopup from "./ContinueAddingPopup";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import AuthContext from "../../context/auth/AuthContext";
import styles from "./missingDocument.module.css";
import { isKycReviewFile as isKycReviewFileUtil } from "../../Utils/kycUtils";
import {
  Badge,
  Button,
  Icon,
  Skeleton,
  SkeletonList,
  SkeletonText,
} from "../../components/design";
import MissingDocumentsExplanationBlock from "../../components/Onboarding/MissingDocumentsExplanationBlock";
import {
  getOwnRetaAnalysisDocs,
  getRetaSlotIndex,
  isRetaRequired,
  RETA_ORDER,
} from "../../Utils/retaRequirement";

// Company login tenants: Form 303 slots, same order as
// getRequiredVatQuarterPeriods (most recent first).
const VAT_QUARTER_KEYS = [
  constants.firstQuarterVat,
  constants.secondQuarterVat,
  constants.thirdQuarterVat,
];

const PAYSLIP_PERIOD_KEYS = [
  constants.firstMonthPayslip,
  constants.secondMonthPayslip,
  constants.thirdMonthPayslip,
];

const REVIEW_TAGS = {
  scan: "fileReviewTagScan",
  duplicate: "fileReviewTagDuplicate",
  thirdParty: "fileReviewTagThirdParty",
};

const getFileNameFromUrl = (url) => {
  if (!haveValue(url) || typeof url !== "string") return "";

  try {
    const pathname = new URL(url).pathname;
    return pathname?.split("/").filter(Boolean).pop() ?? "";
  } catch (error) {
    return url?.split("?")[0]?.split("/").filter(Boolean).pop() ?? "";
  }
};

const getProfileDocuments = (profile) => {
  const buckets = profile?.report?.documentBuckets ?? [];

  if (buckets.length > 0) {
    return buckets.flatMap((bucket) =>
      (bucket?.documents ?? []).map((doc) => ({
        ...doc,
        bucketName: bucket?.bucketName ?? doc?.bucketName,
      }))
    );
  }

  return profile?.report?.allDocuments ?? [];
};

const buildForensicMap = (user) => {
  const forensicReports =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const forensicMap = {};

  forensicReports.forEach((entry) => {
    (entry?.report?.documents_list ?? []).forEach((doc) => {
      const extractedFileName = doc?.url?.split(
        `/${process.env.REACT_APP_CDN_CONTAINER}/`
      )[1];
      const fileName = extractedFileName?.split("?")[0];

      if (fileName) {
        forensicMap[fileName] = doc;
      }
    });
  });

  return forensicMap;
};

const isScannedDocument = (fileName, forensicMap) => {
  const report = forensicMap?.[fileName];
  return (
    report?.documentType === "is_scanned_document" ||
    report?.isScannedDocument === "yes"
  );
};

const buildFilesToReview = (profiles, forensicMap, user) => {
  const files = [];
  const seen = new Set();

  profiles.forEach((profile) => {
    const isUploader = profile?.data?.is_uploader;
    const ownerName =
      (
        profile?.data?.owner ??
        profile?.owner ??
        profile?.data?.owner_name ??
        profile?.data?.name ??
        "unknown"
      )
        .toString()
        .trim() || "unknown";
    const ownerType = profile?.data?.owner_type || profile?.owner_type;

    getProfileDocuments(profile).forEach((doc) => {
      const url = doc?.data?.url ?? doc?.url;
      const fileName = getFileNameFromUrl(url) || doc?.documentName;
      const fileKey = `${ownerName}-${fileName}-${doc?.documentId ?? url}`;

      if (!fileName || seen.has(fileKey)) return;

      let tag = null;

      if (doc?.isDuplicate) {
        tag = "duplicate";
      } else if (
        (!isUploader || ownerType === "legal_entity") &&
        !isCompanyTenantOwnDocument(user, { isUploader, doc })
      ) {
        tag = "thirdParty";
      } else if (isScannedDocument(fileName, forensicMap)) {
        tag = "scan";
      }

      if (!tag) return;

      seen.add(fileKey);
      files.push({
        id: fileKey,
        fileName,
        tag,
        ownerName,
        documentName:
          doc?.localDocumentName?.en ||
          doc?.documentNameTranslated?.en ||
          doc?.documentName,
        documentId: doc?.documentId,
        bucketName: doc?.bucketName,
      });
    });
  });

  return files;
};

const MissingDocument = ({
  documentAnalysisResponse,
  docAnalysisLoading,
  documentAnalysisStatus,
  formHandler,
  user,
  selectedOnboardingUsers,
  setSelectedOnboardingUsers,
  updateUserProfile,
  isGuarantor,
  isEditMode,
  usedFor,
  modules,
  handleSubmitFinalStep,
  isDummySignup,
  setStep,
  deletedReviewFileIds: sharedDeletedReviewFileIds,
  setDeletedReviewFileIds: setSharedDeletedReviewFileIds,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  // Analysis is shared from OnboardingMain: single fetch + cache, repolled on upload.
  const {
    upsertSelectedOnboardingUsers,
    deleteDuplicateDocuments,
    deleteDocument,
    fetchTenantDocs,
    fetchMissingDocumentsNote,
    saveMissingDocumentsNote,
  } = useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [missingDocuments, setMissingDocuments] = useState([]);
  const [filesToReview, setFilesToReview] = useState([]);
  const [totalUploadedFiles, setTotalUploadedFiles] = useState(0);
  const [matchedDocumentsCount, setMatchedDocumentsCount] = useState(0);
  const [submittingLoading, setSubmittingLoading] = useState(false);
  // Keys of the missing documents the tenant selected to explain. The note
  // covers all selected documents with a single message shared with landlords.
  const [selectedExplainedKeys, setSelectedExplainedKeys] = useState([]);
  // Single explanation message for all selected documents. Shown to landlords;
  // never marks a document as received and never changes the score.
  const [explanationMessage, setExplanationMessage] = useState(
    user?.missingDocumentReason ?? ""
  );
  // The tenant's existing note (if any) used to pre-select documents and to
  // decide whether Continue must delete a note (deselected everything).
  const [existingMissingDocNote, setExistingMissingDocNote] = useState(null);
  const [loadedNoteTenantId, setLoadedNoteTenantId] = useState(null);
  const [noteLoadError, setNoteLoadError] = useState(false);
  const [noteLoadAttempt, setNoteLoadAttempt] = useState(0);
  const [missingListAnalysis, setMissingListAnalysis] = useState(null);
  const [missingDocNoteInitialized, setMissingDocNoteInitialized] =
    useState(false);
  const [showSkipStepPopup, setShowSkipStepPopup] = useState(false);
  const [skipStepLoading, setSkipStepLoading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isDeletingAllReview, setIsDeletingAllReview] = useState(false);
  const [localDeletedReviewFileIds, setLocalDeletedReviewFileIds] = useState(
    []
  );
  const deletedReviewFileIds =
    sharedDeletedReviewFileIds ?? localDeletedReviewFileIds;
  const setDeletedReviewFileIds =
    setSharedDeletedReviewFileIds ?? setLocalDeletedReviewFileIds;
  const [tenantDocsData, setTenantDocsData] = useState([]);
  // The review list hides Clave and ID files by matching them against these
  // docs, so it is only right once they have loaded. Until the first load,
  // keep the skeleton up instead of showing every analysed file for a moment.
  const [tenantDocsLoaded, setTenantDocsLoaded] = useState(false);
  const analysisFailureMessage = _t(
    "documentAnalysisFailedPleaseReUpload",
    "Text used in onboarding flow"
  );
  // True only when landing here via a fresh submit from RequiredDocuments
  // (step 3 sets the flag). A page refresh on this step has no flag, so it
  // gets a generic loader instead of the document-analysis loader.
  const [cameFromRequiredSubmit] = useState(() => {
    try {
      return sessionStorage.getItem("docAnalysisFromRequired") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.removeItem("docAnalysisFromRequired");
    } catch {}
  }, []);

  // Show "still processing" escape hatch only if shared loading runs long.
  useEffect(() => {
    if (!docAnalysisLoading) return;
    const t = setTimeout(() => setShowSkipStepPopup(true), 20000);
    return () => clearTimeout(t);
  }, [docAnalysisLoading]);

  useEffect(() => {
    if (isDummySignup) return;

    if (documentAnalysisStatus === "failed") {
      toast.error(analysisFailureMessage);
    }
  }, [isDummySignup, documentAnalysisStatus, analysisFailureMessage]);

  useEffect(() => {
    if (haveValue(documentAnalysisResponse)) {
      loadUserTenant("tenant");
    }
  }, [documentAnalysisResponse]);

  useEffect(() => {
    if (!user?._id) return;
    const fetchDocs = async () => {
      try {
        const res = await fetchTenantDocs(user._id, false, isGuarantor);
        const docs = res?.data?.success_res?.tenantDocs ?? [];
        setTenantDocsData(Array.isArray(docs) ? docs : []);
      } catch {
        setTenantDocsData([]);
      } finally {
        setTenantDocsLoaded(true);
      }
    };
    fetchDocs();
  }, [user?._id, isGuarantor, documentAnalysisResponse]);

  // Load the tenant's existing explanation note once, so the sign-up step can
  // pre-select the same documents and pre-fill the message. One note per
  // tenant: saving here replaces the profile note and vice versa.
  useEffect(() => {
    if (!user?._id || !fetchMissingDocumentsNote) return;
    let cancelled = false;
    setLoadedNoteTenantId(null);
    setNoteLoadError(false);
    setMissingDocNoteInitialized(false);
    setExistingMissingDocNote(null);
    setSelectedExplainedKeys([]);
    setExplanationMessage(user?.missingDocumentReason ?? "");
    const loadExistingNote = async () => {
      try {
        const note = await fetchMissingDocumentsNote(user._id);
        if (cancelled) return;
        setExistingMissingDocNote(note ?? null);
        setLoadedNoteTenantId(user._id);
      } catch {
        if (!cancelled) setNoteLoadError(true);
      }
    };
    loadExistingNote();
    return () => {
      cancelled = true;
    };
  }, [user?._id, fetchMissingDocumentsNote, noteLoadAttempt]);

  // Pre-select documents and message from the existing note once both the
  // note and the computed missing list are available. Only keys that are
  // still missing stay selected; detected-later documents drop out.
  // Gated on documentAnalysisResponse because that is what produces the
  // missing list (empty before analysis resolves).
  useEffect(() => {
    if (missingDocNoteInitialized) return;
    if (!haveValue(documentAnalysisResponse)) return;
    if (loadedNoteTenantId !== user?._id) return;
    if (missingListAnalysis !== documentAnalysisResponse) return;
    if (!existingMissingDocNote) {
      setMissingDocNoteInitialized(true);
      return;
    }
    // Canonical-key pre-selection: the note may have been written from the
    // profile (different document vocabulary) and still match here.
    const explainedKeys = getExplainedDocCanonicalKeys(existingMissingDocNote);
    setSelectedExplainedKeys(
      missingDocuments.filter((docKey) =>
        isCanonicalKeyCovered(explainedKeys, docKey)
      )
    );
    setExplanationMessage(existingMissingDocNote.explanationMessage ?? "");
    setMissingDocNoteInitialized(true);
  }, [
    existingMissingDocNote,
    loadedNoteTenantId,
    user?._id,
    missingListAnalysis,
    missingDocuments,
    missingDocNoteInitialized,
    documentAnalysisResponse,
  ]);

  const tenantDocsUrlMap = useMemo(
    () => new Map(tenantDocsData.map((doc) => [doc?.file_name, doc?.url])),
    [tenantDocsData]
  );
  const tenantDocsByFileName = useMemo(
    () => new Map(tenantDocsData.map((doc) => [doc?.file_name, doc])),
    [tenantDocsData]
  );
  const forensicNameByFile = useMemo(() => {
    const map = new Map();
    const forensicReports =
      user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
    forensicReports.forEach((entry) => {
      const keyFactors = entry?.report?.keyFactors?.keyFactors ?? [];
      const name = (Array.isArray(keyFactors) ? keyFactors : []).find(
        (kf) => kf?.type === "Name" || kf?.key === "Name"
      )?.dataInJSON?.name;
      if (!name) return;
      (entry?.report?.documents_list ?? []).forEach((doc) => {
        const fileName = getFileNameFromUrl(doc?.url);
        if (fileName && !map.has(fileName)) map.set(fileName, name);
      });
    });
    return map;
  }, [user]);

  const filesToReviewWithSignedUrls = useMemo(
    () =>
      filesToReview
        .filter(
          (file) =>
            !deletedReviewFileIds.includes(file.id) &&
            !isKycReviewFileUtil(file, tenantDocsData) &&
            (file.bucketName || "").toLowerCase() !== "identification" &&
            (file.tag === "outdatedPayslip" ||
              !tenantDocsData.some(
                (doc) =>
                  doc?.from === "clave" && doc?.file_name === file.fileName
              ))
        )
        .map((file) => {
          const tenantDoc = tenantDocsByFileName.get(file.fileName);
          const displayOwnerName = (
            file.ownerName ||
            tenantDoc?.uploadedFor ||
            forensicNameByFile.get(file.fileName) ||
            ""
          )
            .toString()
            .trim();
          return {
            ...file,
            url: tenantDocsUrlMap.get(file.fileName) ?? null,
            displayOwnerName,
          };
        }),
    [
      filesToReview,
      deletedReviewFileIds,
      tenantDocsUrlMap,
      tenantDocsData,
      tenantDocsByFileName,
      forensicNameByFile,
    ]
  );

  const thirdPartyGroups = useMemo(() => {
    const groups = new Map();
    filesToReviewWithSignedUrls
      .filter((file) => file.tag === "thirdParty")
      .forEach((file) => {
        const ownerKey =
          (file.displayOwnerName || file.ownerName || "").trim() ||
          _t("unknownOwner", "Unknown owner");
        if (!groups.has(ownerKey)) groups.set(ownerKey, []);
        groups.get(ownerKey).push(file);
      });
    return [...groups.entries()];
  }, [filesToReviewWithSignedUrls, _t]);

  const nonThirdPartyFiles = useMemo(
    () =>
      filesToReviewWithSignedUrls.filter((file) => file.tag !== "thirdParty"),
    [filesToReviewWithSignedUrls]
  );

  const isKycReviewFile = (file) => isKycReviewFileUtil(file, tenantDocsData);

  useEffect(() => {
    if (haveValue(documentAnalysisResponse) && !isDummySignup) {
      let uploader = {};
      let otherUsersList = [];
      const docResponse =
        documentAnalysisResponse?.document_analysis?.profileReports ??
        documentAnalysisResponse?.profileReports ??
        [];

      for (const dataItem of docResponse) {
        if (dataItem?.data?.is_uploader) {
          uploader = dataItem;
        } else if (
          // Company login tenants: a signatory (only ID documents) is not
          // another person to ask about.
          !(
            user?.employment_status === constants.company_tenant &&
            getProfileDocuments(dataItem).length > 0 &&
            getProfileDocuments(dataItem).every(isIdentityDocument)
          )
        ) {
          otherUsersList.push(dataItem);
        }
      }

      const profiles = [uploader, ...otherUsersList].filter(
        (profile) => profile && Object.keys(profile).length > 0
      );
      const forensicMap = buildForensicMap(user);

      let userAllDocuments = getDocumentsFromEmploymentType(user, true);

      const selectedProtectedDocs =
        user?.tenant_extra_field_id?.[constants.public_housing_fileFrom] ?? [];
      const hasNoSpecialCircumstances =
        selectedProtectedDocs.includes(constants.no_special_circumstances) ||
        selectedProtectedDocs.includes("no") ||
        selectedProtectedDocs.some(
          (v) => v?.toLowerCase?.() === "no special circumstances"
        );

      const residencyDoc = constants.certificate_of_residency_registration;
      const selectedBaseProtectedDocs = selectedProtectedDocs.filter(
        (doc) =>
          doc &&
          doc !== constants.no_special_circumstances &&
          doc !== residencyDoc
      );

      const effectiveProtectedDocs =
        selectedBaseProtectedDocs.length > 0
          ? [...selectedBaseProtectedDocs, residencyDoc]
          : hasNoSpecialCircumstances
            ? [residencyDoc]
            : [];

      const protectedHousingDocs = Object.keys(
        docsData.PROTECTED_HOUSING
      ).filter((key) => key !== constants.no_special_circumstances);

      const docsToRemove = [
        ...protectedHousingDocs.filter(
          (key) => !effectiveProtectedDocs.includes(key)
        ),
        constants.no_special_circumstances,
        "no",
        "No special circumstances",
      ];

      const otherDocsOfUploader = [];
      (uploader?.report?.otherDocuments ?? []).forEach((docEntry) => {
        otherDocsOfUploader.push(docEntry?.data?.name);
      });

      userAllDocuments = Object.keys(userAllDocuments)
        .filter(
          (label) =>
            !docsToRemove.includes(label) &&
            !otherDocsOfUploader.includes(label)
        )
        .reduce((obj, label) => {
          obj[label] = userAllDocuments[label];
          return obj;
        }, {});

      effectiveProtectedDocs.forEach((docType) => {
        if (!userAllDocuments[docType]) {
          userAllDocuments[docType] = docsData.PROTECTED_HOUSING[docType];
        }
      });

      const matchedDocuments = uploader?.report?.matchedDocuments ?? [];
      const matchedDocumentsMapped = new Map();

      for (const doc of matchedDocuments) {
        if (doc?.documentName) {
          let mapName = docsMap?.[doc?.documentName] ?? doc?.documentName;
          // for company type below
          if (
            user?.employment_status === constants.company &&
            mapName === constants.quarterly_vat
          ) {
            const periodQuarter =
              doc?.documentAnalysisData?.period_quarter ??
              doc?.data?.period_quarter ??
              doc?.period_quarter;

            if (periodQuarter) {
              mapName = `${mapName} Q${periodQuarter}`;
            }
            console.log("periodQuarter", periodQuarter, "mapName", mapName);
          }
          // for company type above
          matchedDocumentsMapped.set(mapName, doc);
        }
      }

      const expectedKeys = Object.keys(userAllDocuments);
      let matchedKeys = Array.from(matchedDocumentsMapped.keys());

      if (user?.employment_status === constants.company_tenant) {
        const requiredVatQuarters = getRequiredVatQuarterPeriods();
        for (const doc of matchedDocuments) {
          const mapName = docsMap?.[doc?.documentName] ?? doc?.documentName;
          if (mapName !== constants.quarterly_vat) continue;
          const periodYear = Number(doc?.period_year);
          const periodQuarter = Number(
            doc?.documentAnalysisData?.period_quarter ??
              doc?.data?.period_quarter ??
              doc?.period_quarter
          );
          const quarterIndex = requiredVatQuarters.findIndex(
            (period) =>
              period.period_year === periodYear &&
              period.period_quarter === periodQuarter
          );
          if (
            quarterIndex >= 0 &&
            !matchedKeys.includes(VAT_QUARTER_KEYS[quarterIndex])
          ) {
            matchedKeys.push(VAT_QUARTER_KEYS[quarterIndex]);
          }
        }
      }

      // Freelancers: RETA receipts fill the slot of their month.
      if (isRetaRequired(user)) {
        for (const doc of getOwnRetaAnalysisDocs([uploader])) {
          const retaSlotIndex = getRetaSlotIndex({
            period_year: doc?.period_year ?? doc?.data?.period_year,
            period_month: doc?.period_month ?? doc?.data?.period_month,
          });
          if (
            retaSlotIndex >= 0 &&
            !matchedKeys.includes(RETA_ORDER[retaSlotIndex])
          ) {
            matchedKeys.push(RETA_ORDER[retaSlotIndex]);
          }
        }
      }

      const isThreePayslipRequired =
        user?.employment_status === constants.employed ||
        user?.employment_status === constants.public_servant;
      const outdatedPayslipReviewFiles = [];

      if (isThreePayslipRequired) {
        const requiredPayslipPeriods = getRequiredPayslipPeriods();
        const matchedPeriodIndexes = new Set();

        for (const doc of matchedDocuments) {
          if (doc?.documentName?.toLowerCase()?.includes("payslip")) {
            const payslipPeriodYear = doc?.period_year;
            const payslipPeriodMonth = doc?.period_month;

            if (
              payslipPeriodYear &&
              payslipPeriodMonth &&
              requiredPayslipPeriods.length > 0
            ) {
              const matchedPeriodIndex = requiredPayslipPeriods.findIndex(
                (period) =>
                  period.period_year === payslipPeriodYear &&
                  period.period_month === payslipPeriodMonth
              );

              if (
                matchedPeriodIndex >= 0 &&
                !matchedPeriodIndexes.has(matchedPeriodIndex)
              ) {
                matchedPeriodIndexes.add(matchedPeriodIndex);
                const payslipKey = PAYSLIP_PERIOD_KEYS[matchedPeriodIndex];
                if (!matchedKeys.includes(payslipKey)) {
                  matchedKeys.push(payslipKey);
                }
              }
            }
          }
        }

        const existingPayslipKeys = PAYSLIP_PERIOD_KEYS.filter((key) =>
          matchedKeys.includes(key)
        );

        for (const existingKey of existingPayslipKeys) {
          const index = PAYSLIP_PERIOD_KEYS.indexOf(existingKey);
          if (index >= 0 && !matchedPeriodIndexes.has(index)) {
            matchedPeriodIndexes.add(index);
          }
        }

        const payslipPeriodByFileName = new Map();
        matchedDocuments.forEach((doc) => {
          const fileName = getFileNameFromUrl(doc?.data?.url ?? doc?.url);
          if (fileName && doc?.period_year && doc?.period_month) {
            payslipPeriodByFileName.set(fileName, {
              period_year: doc.period_year,
              period_month: doc.period_month,
            });
          }
        });

        tenantDocsData.forEach((tenantDoc) => {
          if (!tenantDoc?.type?.toLowerCase?.().includes("payslip")) return;

          const period = payslipPeriodByFileName.get(tenantDoc?.file_name);
          if (!period) return;

          const payslip = { ...tenantDoc, ...period };
          const warning = getPayslipDateWarning(
            payslip,
            requiredPayslipPeriods,
            _t
          );

          if (!warning || !tenantDoc?.file_name) return;

          outdatedPayslipReviewFiles.push({
            id: tenantDoc?._id || `outdated-${tenantDoc.file_name}`,
            fileName: tenantDoc.file_name,
            tag: "outdatedPayslip",
            tagLabel: warning,
            documentId: tenantDoc?._id,
          });
        });
      }

      const missingKeys = expectedKeys.filter(
        (key) =>
          key !== constants.no_special_circumstances &&
          key !== "no" &&
          key.toLowerCase() !== "no special circumstances" &&
          !matchedKeys.includes(key)
      );

      const reviewFiles = buildFilesToReview(profiles, forensicMap, user);
      const reviewFileNames = new Set(reviewFiles.map((file) => file.fileName));
      const allReviewFiles = [
        ...reviewFiles,
        ...outdatedPayslipReviewFiles.filter(
          (file) => !reviewFileNames.has(file.fileName)
        ),
      ];
      const visibleReviewFiles = allReviewFiles.filter(
        (file) =>
          !isKycReviewFileUtil(file, tenantDocsData) &&
          (file.bucketName || "").toLowerCase() !== "identification" &&
          (file.tag === "outdatedPayslip" ||
            !tenantDocsData.some(
              (doc) => doc?.from === "clave" && doc?.file_name === file.fileName
            ))
      );
      // Count files across all profiles (uploader + other users) – fallback to
      // tenantDocs / review + matched when buckets are empty (analysis shape varies)
      const allProfileDocsCount = profiles.reduce(
        (sum, p) => sum + getProfileDocuments(p).length,
        0
      );
      const fallbackCount =
        allReviewFiles.length + matchedKeys.length ||
        (Array.isArray(tenantDocsData) ? tenantDocsData.length : 0);
      const uploadedFilesCount = allProfileDocsCount || fallbackCount;

      // matched count is more robust as expected - missing (covers payslip period mapping)
      const matchedCount =
        matchedKeys.length ||
        Math.max(expectedKeys.length - missingKeys.length, 0);

      if (
        missingKeys.length === 0 &&
        otherUsersList.length === 0 &&
        visibleReviewFiles.length === 0
      ) {
        handleSkipStep();
      }

      setMissingDocuments(missingKeys);
      setMissingListAnalysis(documentAnalysisResponse);
      setFilesToReview(allReviewFiles);
      setTotalUploadedFiles(uploadedFilesCount);
      setMatchedDocumentsCount(matchedCount);
    }
  }, [documentAnalysisResponse, user, tenantDocsData]);
  // Toggle one missing document in the explanation selection. The message is
  // shared across all selected documents (one comment for the whole note).
  // Selection is canonical-key based (one entry per document slot) so equivalent
  // keys never duplicate each other.
  const toggleExplainedDocumentKey = (docKey) => {
    const toggledCanonicalKey = canonicalizeMissingDocKey(docKey);
    setSelectedExplainedKeys((currentSelectedKeys) => {
      const isCurrentlySelected = currentSelectedKeys.some(
        (selectedKey) =>
          canonicalizeMissingDocKey(selectedKey) === toggledCanonicalKey
      );
      if (isCurrentlySelected) {
        return currentSelectedKeys.filter(
          (selectedKey) =>
            canonicalizeMissingDocKey(selectedKey) !== toggledCanonicalKey
        );
      }
      return [...currentSelectedKeys, docKey];
    });
  };

  const handleNextButton = async () => {
    if (isDummySignup) {
      setStep(4);
      return;
    }
    if (!missingDocNoteInitialized || loadedNoteTenantId !== user?._id) return;
    try {
      setSubmittingLoading(true);
      const role = localStorage.getItem("usertype");
      const trimmedExplanation = explanationMessage.trim();

      // Ticket: Continue with selected documents but an empty comment is
      // blocked with a toast (both normal sign-up and Avalon iframe).
      if (selectedExplainedKeys.length > 0 && !trimmedExplanation) {
        toast.error(
          _t(
            "missingDocExplainRequired",
            "Add a short explanation for the landlord."
          )
        );
        return;
      }

      // Persist the single explanation note, shared with every landlord.
      // Unselecting everything and continuing deletes the note, so reminders
      // resume for those documents. The note never marks a document as
      // received and never changes the score.
      const noteOrigin =
        isEditMode || usedFor === "tenantChecks" ? "profile" : "signup";
      const hasNoteToWrite =
        selectedExplainedKeys.length > 0 || existingMissingDocNote;
      if (hasNoteToWrite) {
        try {
          await saveMissingDocumentsNote(user?._id, {
            explainedDocuments: selectedExplainedKeys.map((docKey) => ({
              documentKey: docKey,
              documentLabel: docKey,
            })),
            explanationMessage: trimmedExplanation,
            origin: noteOrigin,
          });
        } catch (noteError) {
          toast.error(_t("errorSubmitting"));
          return;
        }
      }

      // Keep the legacy free-text field in sync while older readers (Summary,
      // profile) still display it. Untouched when the tenant never used the
      // block, so we never erase a legacy message by accident.
      const reasonPayload = { role };
      if (hasNoteToWrite) {
        reasonPayload.missingDocumentReason =
          selectedExplainedKeys.length > 0
            ? trimmedExplanation.slice(0, 500)
            : "";
      }
      const payload = {
        tenant_id: user?._id,
        users: selectedOnboardingUsers,
      };
      await upsertSelectedOnboardingUsers(payload);
      if (isEditMode) {
        await updateUserProfile(reasonPayload);
        navigate("/home");
      } else if (usedFor === "tenantChecks") {
        await updateUserProfile({ ...reasonPayload, id: user?._id });
        await formHandler.next();
      } else {
        // Company: BasicInfo -> Documents -> profile completed (no Bank/Guarantor/Application)
        const isCompany = user?.employment_status === constants.company;
        const shouldCompleteOnboarding = isCompany;

        if (shouldCompleteOnboarding) {
          // Complete onboarding for companies
          trackClarityEvent("tenant_missing_document_step_ended");

          // Submit consents for companies
          //   try {
          //     await submitConsents({
          //       gdpr_consent: true,
          //       skor_consent: true,
          //       declaration_consent: true,
          //     });
          //   } catch (consentError) {
          //     console.log("Error submitting consents:", consentError);
          //   }

          let payload2 = {
            step: 9,
            role: localStorage.getItem("usertype"),
            is_edit: false,
            profile_completed: true,
            missingDocumentReason: reasonPayload.missingDocumentReason,
          };
          await updateUserProfile(payload2);
          await loadUserTenant("tenant");
          if (formHandler?.goTo) formHandler.goTo(9);
          else navigate("/home?welcome=true");
        } else {
          // Normal flow for non-companies
          // Company login tenants go on to the occupants step (11).
          const nextStep =
            user?.employment_status === constants.company_tenant
              ? 11
              : user?.skip_steps?.includes(
                    constants.SKIP_STEPS_ENUM.bank_onboarding
                  )
                ? 7
                : 6;
          await updateUserProfile({
            step: nextStep,
            ...reasonPayload,
          });
          trackClarityEvent("tenant_missing_document_step_ended");
          setTimeout(() => {
            trackClarityEvent(
              nextStep === 7
                ? "tenant_guarantor_step_started"
                : "tenant_bank_connect_step_started"
            );
          }, constants.CLARITY_DELAY);
        }
      }
    } catch (error) {
      console.log("Error in submitting ", error);
    } finally {
      setSubmittingLoading(false);
    }
  };

  const handleSkipStep = async () => {
    try {
      setSkipStepLoading(true);
      if (usedFor === "tenantChecks") {
        await formHandler.next();
        return;
      }
      const role = localStorage.getItem("usertype");

      const isCompany = user?.employment_status === constants.company;
      const shouldCompleteOnboarding = isCompany;

      if (shouldCompleteOnboarding) {
        // Complete onboarding for companies
        trackClarityEvent("tenant_missing_document_step_ended");

        // Submit consents for companies
        // try {
        //   await submitConsents({
        //     gdpr_consent: true,
        //     skor_consent: true,
        //     declaration_consent: true,
        //   });
        // } catch (consentError) {
        //   console.log("Error submitting consents:", consentError);
        // }

        let payload = {
          step: 9,
          role: localStorage.getItem("usertype"),
          is_edit: false,
          profile_completed: true,
        };
        await updateUserProfile(payload);
        await loadUserTenant("tenant");
        if (formHandler?.goTo) formHandler.goTo(9);
        else navigate("/home?welcome=true");
      } else {
        // Normal flow for non-companies
        // Company login tenants go on to the occupants step (11).
        const nextStep =
          user?.employment_status === constants.company_tenant
            ? 11
            : user?.skip_steps?.includes(
                  constants.SKIP_STEPS_ENUM.bank_onboarding
                )
              ? 7
              : 6;
        await updateUserProfile({ step: nextStep, role });
        trackClarityEvent("tenant_missing_document_step_ended");
        setTimeout(() => {
          trackClarityEvent(
            nextStep === 7
              ? "tenant_guarantor_step_started"
              : "tenant_bank_connect_step_started"
          );
        }, constants.CLARITY_DELAY);
      }
    } catch (error) {
      console.log("Error skipping step", error);
    } finally {
      setSkipStepLoading(false);
    }
  };

  const handleReviewDocuments = () => {
    // Back from MissingDocument (step 4) must go to RequiredDocuments (step 3),
    // even when step 3 is marked as skipped — otherwise generic prev()
    // skips it and lands on KYC verification (step 2).
    if (typeof formHandler?.goTo === "function") {
      formHandler.goTo(3);
    } else {
      formHandler.prev();
    }
    trackClarityEvent("tenant_reupload_document_step");
  };

  const deleteSingleReviewFile = async (file, docsSnapshot) => {
    if (isKycReviewFile(file)) return null;

    if (file.tag === "duplicate") {
      await deleteDuplicateDocuments({
        owner: file.displayOwnerName || file.ownerName,
        documentName: file.documentName,
      });
    } else {
      let docs = docsSnapshot ?? tenantDocsData;
      if (!docs.length) {
        const res = await fetchTenantDocs(user?._id, false, isGuarantor);
        docs = res?.data?.success_res?.tenantDocs ?? [];
      }
      const matchedDoc = (Array.isArray(docs) ? docs : []).find(
        (doc) => doc?.file_name === file.fileName
      );

      if (!matchedDoc?._id) {
        throw new Error("document-not-found");
      }

      await deleteDocument(matchedDoc._id, user?._id);
    }

    return file.id;
  };

  const handleDeleteReviewFile = async (file) => {
    if (isKycReviewFile(file)) return;
    try {
      setDeletingFileId(file.id);
      const wasDuplicate = file.tag === "duplicate";
      await deleteSingleReviewFile(file);
      toast.success(
        wasDuplicate
          ? _t("duplicateDocDeletedSuccessfully")
          : _t("documentDeletedSuccessfully", "Document deleted")
      );
      if (!wasDuplicate) {
        try {
          const res = await fetchTenantDocs(user?._id, false, isGuarantor);
          setTenantDocsData(res?.data?.success_res?.tenantDocs ?? []);
        } catch {}
      }

      setDeletedReviewFileIds((current) => [...current, file.id]);
      setFilesToReview((prev) => prev.filter((item) => item.id !== file.id));
      loadUserTenant("tenant");
    } catch (error) {
      toast.error(_t("errorWhileDeletingDuplicateDoc"));
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteAllReviewFiles = async () => {
    const deletableFiles = filesToReviewWithSignedUrls.filter(
      (file) => !isKycReviewFile(file)
    );
    if (deletableFiles.length === 0 || isDeletingAllReview) return;
    try {
      setIsDeletingAllReview(true);
      let docsSnapshot = [...tenantDocsData];
      if (!docsSnapshot.length) {
        try {
          const res = await fetchTenantDocs(user?._id, false, isGuarantor);
          docsSnapshot = res?.data?.success_res?.tenantDocs ?? [];
        } catch {}
      }
      const deletedIds = [];

      for await (const file of deletableFiles) {
        try {
          const deletedId = await deleteSingleReviewFile(file, docsSnapshot);
          if (deletedId) {
            deletedIds.push(deletedId);
            docsSnapshot = docsSnapshot.filter(
              (doc) => doc?.file_name !== file.fileName
            );
          }
        } catch {
          // continue with next file — report partial failure at the end
        }
      }

      if (deletedIds.length > 0) {
        setDeletedReviewFileIds((current) => [...current, ...deletedIds]);
        setFilesToReview((prev) =>
          prev.filter((item) => !deletedIds.includes(item.id))
        );
        toast.success(_t("documentDeletedSuccessfully", "Document deleted"));
        try {
          const res = await fetchTenantDocs(user?._id, false, isGuarantor);
          setTenantDocsData(res?.data?.success_res?.tenantDocs ?? []);
        } catch {}
        loadUserTenant("tenant");
      }
      if (deletedIds.length < deletableFiles.length) {
        toast.error(_t("errorWhileDeletingDuplicateDoc"));
      }
    } finally {
      setIsDeletingAllReview(false);
    }
  };

  const showMissingSection = missingDocuments.length > 0;
  const showReviewSection = filesToReviewWithSignedUrls.length > 0;
  const isSingleReviewGroup =
    thirdPartyGroups.length + (nonThirdPartyFiles.length > 0 ? 1 : 0) === 1;

  const getOwnerInitials = (name) => {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const renderReviewFile = (file) => (
    <div className={styles.reviewItem} key={file.id}>
      {file.url ? (
        <a
          className={styles.fileName}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          title={removeUuidFromFileName(file.fileName)}
        >
          {removeUuidFromFileName(file.fileName)}
        </a>
      ) : (
        <span
          className={styles.fileName}
          title={removeUuidFromFileName(file.fileName)}
        >
          {removeUuidFromFileName(file.fileName)}
        </span>
      )}
      <Badge
        tone={
          file.tag === "thirdParty"
            ? "warning"
            : file.tag === "duplicate"
              ? "danger"
              : "neutral"
        }
        className={styles.tag}
      >
        {file.tagLabel ??
          _t(REVIEW_TAGS[file.tag], "File review tag in onboarding")}
      </Badge>
      {!isKycReviewFile(file) ? (
        <Button
          variant="dangerGhost"
          size="sm"
          iconOnly
          iconStart={<Icon name="trash" size={16} />}
          className={styles.deleteButton}
          onClick={() => handleDeleteReviewFile(file)}
          disabled={deletingFileId === file.id || isDeletingAllReview}
          aria-label={_t("deleteDocument", "Delete document")}
        />
      ) : (
        <span className={styles.deleteButtonSpacer} aria-hidden="true" />
      )}
    </div>
  );

  if (docAnalysisLoading || !tenantDocsLoaded) {
    if (!docAnalysisLoading || !cameFromRequiredSubmit) {
      return (
        <>
          <div
            className={[
              "popup-overlay",
              "wrapper-modal",
              "modal-fixed-screen",
              styles.overlay,
              isGuarantor ? "guarantorBg" : "",
            ].join(" ")}
          >
            <div className={["missing-doc-popup", styles.card].join(" ")}>
              <div
                role="status"
                aria-label={_t("loading", "Loading")}
                className={styles.loadingSkeleton}
              >
                <Skeleton width="50%" height={24} radius="pill" />
                <SkeletonText lines={2} />
                <SkeletonList rows={3} variant="card" avatar={false} />
              </div>
            </div>
          </div>
          {showSkipStepPopup && (
            <ContinueAddingPopup
              setShowSkipStepPopup={setShowSkipStepPopup}
              handleSkipStep={handleSkipStep}
              skipStepLoading={skipStepLoading}
            />
          )}
        </>
      );
    }
    return (
      <>
        <DocumentAnalysisLoadingPopup
          isGuarantor={isGuarantor}
          showPopup={true}
        />
        {showSkipStepPopup && (
          <ContinueAddingPopup
            setShowSkipStepPopup={setShowSkipStepPopup}
            handleSkipStep={handleSkipStep}
            skipStepLoading={skipStepLoading}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={[
          "popup-overlay",
          "wrapper-modal",
          "modal-fixed-screen",
          styles.overlay,
          isGuarantor ? "guarantorBg" : "",
        ].join(" ")}
      >
        <div
          className={["missing-doc-popup", styles.card].join(" ")}
          onClick={isGuarantor ? (e) => e.stopPropagation() : undefined}
        >
          {(isGuarantor || isEditMode) && (
            <Button
              variant="ghost"
              iconOnly
              iconStart={<Icon name="close" />}
              className={styles.closeButton}
              onClick={() => navigate("/home")}
              aria-label={_t("close", "Close")}
            />
          )}

          <header className={styles.header}>
            <h1>
              {missingDocuments.length > 0
                ? _t(
                    "youAreMissingCountDocuments",
                    "Missing documents page title in onboarding",
                    "translation",
                    { count: missingDocuments.length }
                  )
                : _t(
                    "reviewYourUploadedFiles",
                    "Missing documents page title when only files need review"
                  )}
            </h1>
            {/* <p>
              {_t(
                "missingDocumentsReviewDescription",
                "Missing documents page description in onboarding",
                "translation",
                {
                  total: totalUploadedFiles,
                  matched: matchedDocumentsCount,
                }
              )}
            </p> */}
          </header>

          <div className={styles.sections}>
            {showMissingSection && (
              <section
                className={[styles.section, styles.sectionMissing].join(" ")}
              >
                <div className={styles.sectionHeader}>
                  <Icon
                    name="warning"
                    size={18}
                    className={styles.sectionWarningIcon}
                  />
                  <h2>
                    {_t(
                      "missingDocumentsSectionTitle",
                      "Missing documents section title in onboarding",
                      "translation",
                      { count: missingDocuments.length }
                    )}
                  </h2>
                </div>
                <div
                  className={styles.reasonContentInner}
                  onClick={(event) => event.stopPropagation()}
                >
                  <MissingDocumentsExplanationBlock
                    missingDocuments={missingDocuments}
                    selectedExplainedKeys={selectedExplainedKeys}
                    onToggleExplainedKey={toggleExplainedDocumentKey}
                    explanationMessage={explanationMessage}
                    onExplanationMessageChange={setExplanationMessage}
                    translateDocName={(docKey) => _t(docKey, "", "fixed")}
                    idPrefix="signup-missing-documents"
                    disabled={!missingDocNoteInitialized}
                  />
                </div>
              </section>
            )}

            {showReviewSection && (
              <section
                className={[styles.section, styles.sectionReview].join(" ")}
              >
                <div className={styles.sectionHeader}>
                  <img src="/images/file-icon.svg" alt="" />
                  <h2>
                    {_t(
                      "filesToReviewSectionTitle",
                      "Files to review section title in onboarding",
                      "translation",
                      { count: filesToReviewWithSignedUrls.length }
                    )}
                  </h2>
                  <Button
                    variant="danger"
                    size="sm"
                    className={styles.deleteAllButton}
                    onClick={handleDeleteAllReviewFiles}
                    loading={isDeletingAllReview}
                    disabled={deletingFileId !== null}
                  >
                    {_t("deleteAll", "Delete all")}
                  </Button>
                </div>
                <div
                  className={[
                    styles.reviewList,
                    isSingleReviewGroup ? styles.reviewListSingle : "",
                  ].join(" ")}
                >
                  {thirdPartyGroups.map(([ownerName, files]) => (
                    <div
                      className={[
                        styles.reviewGroup,
                        styles.reviewGroupOwner,
                      ].join(" ")}
                      key={`thirdParty-${ownerName}`}
                    >
                      <div className={styles.reviewGroupHeader}>
                        <span
                          className={styles.reviewAvatar}
                          aria-hidden="true"
                        >
                          {getOwnerInitials(ownerName)}
                        </span>
                        <p className={styles.reviewOwner}>
                          {_t(
                            "thirdPartyDocumentsOf",
                            "Third party documents owner heading",
                            "translation",
                            { owner: ownerName }
                          )}
                        </p>
                        {/* <span className={styles.reviewCount}>
                          {files.length}
                        </span> */}
                      </div>
                      <div className={styles.reviewFiles}>
                        {files.map((file) => renderReviewFile(file))}
                      </div>
                    </div>
                  ))}
                  {nonThirdPartyFiles.length > 0 && (
                    <div
                      className={[
                        styles.reviewGroup,
                        styles.reviewGroupOther,
                      ].join(" ")}
                    >
                      <div className={styles.reviewGroupHeader}>
                        <Icon
                          name="warning"
                          size={18}
                          className={styles.reviewGroupIcon}
                        />
                        <p className={styles.reviewOwner}>
                          {_t(
                            "otherFilesToReview",
                            "Other files to review heading"
                          )}
                        </p>
                        <Badge tone="neutral">
                          {nonThirdPartyFiles.length}
                        </Badge>
                      </div>
                      <div className={styles.reviewFiles}>
                        {nonThirdPartyFiles.map((file) =>
                          renderReviewFile(file)
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          <footer className={styles.footer}>
            <p className={styles.footerNote}>
              {_t(
                "reviewingFilesIsOptional",
                "Footer note on missing documents page in onboarding"
              )}
            </p>
            <div className={styles.footerActions}>
              <Button
                variant="secondary"
                size="lg"
                className={styles.footerButton}
                onClick={handleReviewDocuments}
              >
                {_t(
                  "reviewDocuments",
                  "Review documents button on missing documents page"
                )}
              </Button>
              {noteLoadError && (
                <Button
                  variant="secondary"
                  size="lg"
                  className={styles.footerButton}
                  onClick={() => setNoteLoadAttempt((attempt) => attempt + 1)}
                >
                  {_t(
                    "missingDocRetryLoad",
                    "Retry loading document explanation"
                  )}
                </Button>
              )}
              <Button
                variant="primary"
                size="lg"
                className={styles.footerButton}
                onClick={handleNextButton}
                disabled={
                  submittingLoading ||
                  !missingDocNoteInitialized ||
                  loadedNoteTenantId !== user?._id
                }
                loading={submittingLoading}
              >
                {isEditMode
                  ? _t("submit", "Submit")
                  : _t("continue", "Continue")}
              </Button>
            </div>
          </footer>
        </div>
      </div>
      {showSkipStepPopup && (
        <ContinueAddingPopup
          setShowSkipStepPopup={setShowSkipStepPopup}
          handleSkipStep={handleSkipStep}
          skipStepLoading={skipStepLoading}
        />
      )}
    </>
  );
};

export default MissingDocument;
